import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import WifiSteps from '../components/WifiSteps';
import installDiagram from '../assets/install-diagram-callouts.svg';

// Public install / connect guide (route: /install, no auth). A mobile-friendly
// version of the printed Tank Monitor install card, linked from the claim screen
// (device not online yet) and the dashboard offline banner. The wifi steps double
// as the reset flow for a device that dropped offline.

const BOX_ITEMS = [
  { name: 'Submersible level sensor', note: '16 ft cable, pre-wired', qty: '1' },
  { name: 'Sensor wire guide', note: 'Hooks over the tank lip — longer leg down the outside', qty: '1' },
  { name: 'Tank Level Monitor', note: 'LED level bar, single button, sensor + power inputs — mount out of direct weather', qty: '1' },
  { name: 'Wall bracket', note: 'The monitor slides down onto it', qty: '1' },
  { name: '12 V power adapter', note: 'Plugs into any 120 V outlet', qty: '1' },
  { name: 'Mounting hardware', note: '2 wood screws, drywall kit, 2 zip ties, cable clips', qty: 'kit' },
];

const INSTALL_STEPS = [
  { t: 'Sensor wire guide', d: 'Hook it over the tank lip so it straddles the rim — longer leg down the outside, shorter leg inside. Lay the sensor cable in the groove along the top.' },
  { t: 'Sensor', d: 'Lower it until the tip just touches the tank floor, or leave it suspended just above. Wherever it stops, the tip is the zero point for water height.' },
  { t: 'Zip ties (2)', d: 'Thread through the holes and around the cable so the sensor stays firmly at that depth, without kinking the cable.' },
  { t: 'Wall bracket', d: 'Screw it to the wall at eye level, sheltered from direct weather — wood screws into studs/wood, anchors into drywall.' },
  { t: 'Tank Level Monitor', d: 'Slide it down onto the bracket until it seats.' },
  { t: 'Sensor and power', d: 'Run the cable down the tank, across the ground and up the wall. Plug in the sensor and hand-tighten its lock ring, then plug in the 12 V adapter — both connectors only fit one way.' },
];

export default function InstallGuide() {
  const navigate = useNavigate();

  return (
    <div className="app-container">
      <div className="page-content install-guide">
        <div className="install-guide__head">
          <Logo variant="icon" width={40} />
          <h2>Install your tank monitor</h2>
          <p className="install-guide__lede">
            Mount the sensor and monitor, then get it on wifi. The wifi steps also
            re-connect a device that has dropped offline.
          </p>
        </div>

        {/* What's in the box */}
        <div className="card install-guide__section">
          <h3>What&apos;s in the box</h3>
          <ul className="install-box-list">
            {BOX_ITEMS.map((it) => (
              <li key={it.name}>
                <div className="install-box-list__name">
                  {it.name}
                  <span className="install-box-list__qty">{it.qty === 'kit' ? '1 kit' : `×${it.qty}`}</span>
                </div>
                <div className="install-box-list__note">{it.note}</div>
              </li>
            ))}
          </ul>
          <p className="hint" style={{ marginTop: 10 }}>
            You supply: a drill with bits and a Phillips screwdriver.
          </p>
        </div>

        {/* Typical installation diagram */}
        <div className="card install-guide__section">
          <div className="install-diagram__head">
            <h3>Typical installation</h3>
            <span className="install-diagram__note">Section view · not to scale</span>
          </div>
          <figure className="install-diagram">
            <img
              src={installDiagram}
              alt="Cross-section of a catchment tank showing the install order: (1) sensor wire guide hooked over the tank lip, (2) submersible sensor hanging near the tank floor, (3) zip ties holding the cable at depth, (4) wall bracket mounted at eye level, (5) tank level monitor slid onto the bracket, and (6) the sensor and 12 V power cables running down the tank and up to the monitor."
            />
          </figure>
          <p className="hint" style={{ marginTop: 8 }}>
            The numbers match the install steps below.
          </p>
        </div>

        {/* Install order */}
        <div className="card install-guide__section">
          <h3>Install order</h3>
          <ol className="install-steps">
            {INSTALL_STEPS.map((s) => (
              <li key={s.t}>
                <span className="install-steps__title">{s.t}</span> — {s.d}
              </li>
            ))}
          </ol>
          <p className="hint" style={{ marginTop: 10 }}>
            Typical setup: the sensor hangs inside the tank; the cable runs down the
            tank, across the ground, and up the wall to the monitor and its power
            adapter.
          </p>
        </div>

        {/* Connect to wifi */}
        <div className="card install-guide__section">
          <h3>Connect to wifi</h3>
          <WifiSteps />
          <p className="hint" style={{ marginTop: 10 }}>
            Once it&apos;s connected, the lights stop flashing and the device starts
            reporting within a minute.
          </p>
        </div>

        {/* Claim it. Both paths end on /claim: the QR carries the Device ID and
            Claim Code in the link, the manual path types them in. */}
        <div className="card install-guide__section">
          <h3>Add device to your account</h3>
          <ol className="install-steps">
            <li>
              <span className="install-steps__title">Scan the QR code</span> — it&apos;s
              on the back of the monitor and in your installation guide. Your
              phone&apos;s camera opens the claim page with the Device ID and Claim
              Code already filled in.
            </li>
            <li>
              <span className="install-steps__title">Or enter them yourself</span> — go
              to <Link to="/claim">app.sokowatersystems.com/claim</Link> and type the
              Device ID and Claim Code printed next to the QR code.
            </li>
          </ol>
          <p className="hint" style={{ marginTop: 10 }}>
            Connect it to wifi first: the monitor has to be online to be claimed.
            You&apos;ll be asked to sign in or create an account if you haven&apos;t.
          </p>
        </div>

        <div className="install-guide__actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
            Back
          </button>
          <Link to="/dashboard" className="btn btn-primary">Go to dashboard</Link>
        </div>
      </div>
    </div>
  );
}
